import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Building2,
  Monitor,
  UserSearch,
  Users,
  Settings,
  Loader2,
  X,
} from 'lucide-react';
import api from '../../api/client';
import useAuthStore, { PERMISSIONS } from '../../stores/authStore';

// Global search overlay (Ctrl/Cmd + K). Cafés and fleet PCs are loaded once
// when the palette opens and filtered locally — the same two calls the Cafés
// and PC Registry pages already make. People go through the server-side
// cross-tenant search (superadmin-only, min 2 chars), debounced. Every request
// is aborted when the palette closes, so nothing is left in flight.
const MIN_PEOPLE_QUERY = 2;
const PEOPLE_DEBOUNCE_MS = 250;
const MAX_RESULTS = { cafes: 5, pcs: 5, people: 8 };

const PAGES = [
  { label: 'Cafés', path: '/cafes', icon: Building2, permission: PERMISSIONS.VIEW_CAFE_REGISTRY },
  { label: 'PC Registry', path: '/pcs', icon: Monitor, permission: PERMISSIONS.VIEW_PC_HEALTH },
  { label: 'Users', path: '/users', icon: Users, permission: PERMISSIONS.VIEW_USERS },
  { label: 'Cross-Tenant Search', path: '/cross-tenant-search', icon: UserSearch, superadminOnly: true },
  { label: 'Settings', path: '/settings', icon: Settings },
];

export default function CommandPalette({ onClose }) {
  const navigate = useNavigate();
  const { hasPermission, canSeeAsSuperAdmin } = useAuthStore();

  const canSeeCafes = hasPermission(PERMISSIONS.VIEW_CAFE_REGISTRY);
  const canSeePCs = hasPermission(PERMISSIONS.VIEW_PC_HEALTH);
  const canSeePeople = canSeeAsSuperAdmin();

  const [query, setQuery] = useState('');
  const [cafes, setCafes] = useState([]);
  const [pcs, setPCs] = useState([]);
  // The last people answer and the query it answered. Loading is derived from
  // "answer is for a different query than the box shows", so no state is set
  // synchronously in an effect.
  const [peopleAnswer, setPeopleAnswer] = useState({ q: '', data: [] });
  const [directoryLoading, setDirectoryLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  const q = query.trim();
  const wantsPeople = canSeePeople && q.length >= MIN_PEOPLE_QUERY;
  const people = useMemo(
    () => (wantsPeople ? peopleAnswer.data : []),
    [wantsPeople, peopleAnswer.data],
  );
  const peopleLoading = wantsPeople && peopleAnswer.q !== q;

  const activeRef = useRef(null);

  // Cafés + PCs: one fetch each on open, aborted on close.
  useEffect(() => {
    const controller = new AbortController();
    const load = async (url, enabled, setter) => {
      if (!enabled) return;
      try {
        const res = await api.get(url, { signal: controller.signal });
        setter(res.data || []);
      } catch (err) {
        if (!controller.signal.aborted) console.error(`Search: failed to load ${url}:`, err);
      }
    };
    Promise.all([
      load('/internal/cafes', canSeeCafes, setCafes),
      load('/internal/fleet/pcs', canSeePCs, setPCs),
    ]).finally(() => {
      if (!controller.signal.aborted) setDirectoryLoading(false);
    });
    return () => controller.abort();
  }, [canSeeCafes, canSeePCs]);

  // People: debounced server search. Cleanup aborts the previous request, so
  // a slow answer to "ab" can never overwrite the answer to "abc".
  useEffect(() => {
    if (!wantsPeople) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/internal/cross-tenant-search', {
          params: { q },
          signal: controller.signal,
        });
        setPeopleAnswer({ q, data: res.data || [] });
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error('Search: people lookup failed:', err);
        setPeopleAnswer({ q, data: [] });
      }
    }, PEOPLE_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [q, wantsPeople]);

  const sections = useMemo(() => {
    const term = q.toLowerCase();
    const go = (path) => () => {
      navigate(path);
      onClose();
    };

    const pageItems = PAGES
      .filter((p) => !p.permission || hasPermission(p.permission))
      .filter((p) => !p.superadminOnly || canSeePeople)
      .filter((p) => !term || p.label.toLowerCase().includes(term))
      .map((p) => ({
        key: `page-${p.path}`,
        icon: p.icon,
        title: p.label,
        onSelect: go(p.path),
      }));

    if (!term) return [{ title: 'Go to', items: pageItems }];

    const cafeItems = cafes
      .filter((c) =>
        c.name?.toLowerCase().includes(term) ||
        c.owner_name?.toLowerCase().includes(term) ||
        c.owner_email?.toLowerCase().includes(term) ||
        c.location?.toLowerCase().includes(term))
      .slice(0, MAX_RESULTS.cafes)
      .map((c) => ({
        key: `cafe-${c.id}`,
        icon: Building2,
        title: c.name,
        subtitle: [c.owner_name, c.location].filter(Boolean).join(' · '),
        onSelect: go(`/cafes/${c.id}`),
      }));

    const pcItems = pcs
      .filter((pc) => {
        const name = pc.name || `PC-${pc.id}`;
        return name.toLowerCase().includes(term) ||
          pc.cafe_name?.toLowerCase().includes(term) ||
          pc.ip_address?.includes(term);
      })
      .slice(0, MAX_RESULTS.pcs)
      .map((pc) => {
        const name = pc.name || `PC-${pc.id}`;
        return {
          key: `pc-${pc.id}`,
          icon: Monitor,
          title: name,
          subtitle: [pc.cafe_name || `Café ${pc.cafe_id}`, pc.status].filter(Boolean).join(' · '),
          // The PC's café Details page, with this PC highlighted.
          onSelect: go(`/cafes/${pc.cafe_id}?pc=${encodeURIComponent(name)}`),
        };
      });

    const peopleItems = people.slice(0, MAX_RESULTS.people).map((p) => ({
      key: `person-${p.id}`,
      icon: UserSearch,
      title: p.name,
      subtitle: [p.email, p.role].filter(Boolean).join(' · '),
      onSelect: go(`/cross-tenant-search?q=${encodeURIComponent(p.email)}`),
    }));

    return [
      { title: 'Cafés', items: cafeItems },
      { title: 'PCs', items: pcItems },
      { title: 'People', items: peopleItems },
      { title: 'Pages', items: pageItems },
    ].filter((s) => s.items.length > 0);
  }, [q, cafes, pcs, people, hasPermission, canSeePeople, navigate, onClose]);

  const flatItems = useMemo(() => sections.flatMap((s) => s.items), [sections]);

  // Results can shrink while a row is highlighted (people answers land late),
  // so clamp rather than trust the stored index.
  const active = Math.min(activeIndex, Math.max(flatItems.length - 1, 0));

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(flatItems.length ? (active + 1) % flatItems.length : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(flatItems.length ? (active - 1 + flatItems.length) % flatItems.length : 0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      flatItems[active]?.onSelect();
    }
  };

  const isSearching = q && (directoryLoading || peopleLoading);
  const showEmpty = q && !isSearching && flatItems.length === 0;

  let runningIndex = -1;

  // Portalled to <body>: rendered inside <header> (its own z-index: 50 layer)
  // the overlay's z-index only competes within that layer, so the fixed
  // z-index: 100 sidebar drew on top of it and was never dimmed.
  return createPortal(
    <div className="command-palette-overlay" onClick={onClose}>
      <div className="command-palette" onClick={(e) => e.stopPropagation()}>
        <div className="command-palette__header">
          <Search size={20} className="command-palette__icon" />
          <input
            autoFocus
            type="text"
            placeholder="Search cafés, PCs, people..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleKeyDown}
            className="command-palette__input"
          />
          {isSearching && <Loader2 size={16} className="command-palette__spinner" />}
          <button className="command-palette__close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="command-palette__body">
          {sections.map((section) => (
            <div key={section.title} className="command-palette__section">
              <span className="command-palette__section-title">{section.title}</span>
              {section.items.map((item) => {
                runningIndex += 1;
                const index = runningIndex;
                const Icon = item.icon;
                const isActive = index === active;
                return (
                  <button
                    key={item.key}
                    ref={isActive ? activeRef : null}
                    className={`command-palette__item ${isActive ? 'command-palette__item--active' : ''}`}
                    onClick={item.onSelect}
                    onMouseMove={() => setActiveIndex(index)}
                  >
                    <Icon size={16} />
                    <span className="command-palette__item-text">
                      <span className="command-palette__item-title">{item.title}</span>
                      {item.subtitle && (
                        <span className="command-palette__item-sub">{item.subtitle}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
          {showEmpty && (
            <div className="command-palette__empty">No results for "{q}"</div>
          )}
          {q.length === 1 && canSeePeople && (
            <div className="command-palette__empty">
              Type at least {MIN_PEOPLE_QUERY} characters to search people.
            </div>
          )}
        </div>
        <div className="command-palette__footer">
          <span><kbd>↑↓</kbd> Navigate</span>
          <span><kbd>↵</kbd> Open</span>
          <span><kbd>Esc</kbd> Close</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
